import { Navigate, Link } from '@tanstack/react-router';
import { useAuth } from '../lib/auth';
import { useAccounts } from '../hooks/useAccounts';
import { useTransactionStats, useTransactions } from '../hooks/useTransactions';
import { Navigation } from '../components/layout/Navigation';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import type { Transaction } from '../types/transaction';
import { formatCurrency } from '../lib/currency';
import { useTranslation } from 'react-i18next';

export function Dashboard() {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, user } = useAuth();
  const { data: accounts } = useAccounts();
  const { data: stats } = useTransactionStats();
  const { data: allTransactions } = useTransactions();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-600">{t('common:loading')}</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  const totalBalance = accounts?.reduce((sum, acc) => sum + acc.balance, 0) || 0;
  const recentTransactions = allTransactions?.slice(0, 5) || [];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('common:dashboard')}</h1>
          <p className="text-gray-600 mt-1">{t('common:dashboard_subtitle')}</p>
        </div>

        <div className="grid gap-4 md:grid-cols-3 mb-6">
          <Card>
            <div className="text-sm text-gray-600 mb-1">{t('accounts:total_balance')}</div>
            <div className="text-3xl font-bold text-gray-900">{formatCurrency(totalBalance)}</div>
            <div className="text-xs text-gray-500 mt-2">{accounts?.length || 0} {t('common:accounts')}</div>
          </Card>
          <Card>
            <div className="text-sm text-gray-600 mb-1">{t('transactions:statistics.total_income')}</div>
            <div className="text-3xl font-bold text-green-600">{formatCurrency(stats?.total_income || 0)}</div>
            <div className="text-xs text-gray-500 mt-2">{t('transactions:statistics.all_time')}</div>
          </Card>
          <Card>
            <div className="text-sm text-gray-600 mb-1">{t('transactions:statistics.total_expense')}</div>
            <div className="text-3xl font-bold text-red-600">{formatCurrency(stats?.total_expense || 0)}</div>
            <div className="text-xs text-gray-500 mt-2">{t('transactions:statistics.all_time')}</div>
          </Card>
        </div>

        {(!accounts || accounts.length === 0) && (
          <Card>
            <div className="text-center py-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('common:get_started')}</h3>
              <p className="text-gray-600 mb-4">
                {t('common:get_started_message')}
              </p>
              <Link to="/accounts">
                <Button variant="primary">{t('accounts:create_account')}</Button>
              </Link>
            </div>
          </Card>
        )}

        {accounts && accounts.length > 0 && recentTransactions.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">{t('transactions:recent_transactions')}</h2>
                  <Link to="/transactions" className="text-sm text-blue-600 hover:text-blue-700">
                    {t('common:view_all')} →
                  </Link>
                </div>
                <div className="space-y-3">
                  {recentTransactions.map((transaction) => (
                    <div
                      key={transaction.id}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          transaction.type === 'income'
                            ? 'bg-green-100 text-green-600'
                            : 'bg-red-100 text-red-600'
                        }`}>
                          {transaction.type === 'income' ? '↑' : '↓'}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">
                              {transaction.category ? t(`transactions:categories.${transaction.category}`) : t('common:uncategorized')}
                            </span>
                          </div>
                          {transaction.description && (
                            <p className="text-sm text-gray-500 mt-0.5">{transaction.description}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">{formatDate(transaction.transaction_date)}</p>
                        </div>
                      </div>
                      <div className={`text-right font-semibold ${
                        transaction.type === 'income' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {transaction.type === 'income' ? '+' : '-'} {formatCurrency(transaction.amount)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
        )}
      </div>
    </div>
  );
}
